export interface ServiceOption {
  title: string;
  description: string;
}

export interface ServiceBenefit {
  title: string;
  description: string;
}

export interface ServiceData {
  slug: string;
  title: string;
  img: string;
  tags: string[];
  shortDesc: string;
  subtitle: string;
  intro: string;
  options: ServiceOption[];
  benefits: ServiceBenefit[];
}

export const servicesData: ServiceData[] = [
  {
    slug: "wide-format",
    title: "Wide Format",
    img: "/wide-format-printer.png",
    tags: ["Indoor", "Outdoor", "Custom Sizes"],
    shortDesc: "Large format printing for retail, events, and exhibitions.",
    subtitle: "Make a bold statement at any scale.",
    intro:
      "Wide format printing delivers high-impact visuals at sizes that command attention. Whether you need striking retail displays, event backdrops, or full-scale exhibition stands, All In Solutions sources and manages the perfect wide format solution, handling everything from artwork specs to final delivery so your brand looks outstanding every time.",
    options: [
      {
        title: "Exhibition Displays",
        description:
          "Fabric and vinyl display systems, pop-up walls, and tension fabric structures ideal for trade shows, expos, and corporate events.",
      },
      {
        title: "Retail Graphics",
        description:
          "Window graphics, floor graphics, wall murals, and in-store promotional displays that engage shoppers and reinforce brand identity.",
      },
      {
        title: "Vehicle Wraps",
        description:
          "Full and partial vehicle wraps that transform your fleet into high-visibility rolling advertisements, maximising brand exposure on every road.",
      },
      {
        title: "Outdoor Banners",
        description:
          "Heavy-duty mesh, vinyl, and fabric banners engineered for outdoor conditions: UV resistant, windproof, and built to last.",
      },
      {
        title: "Event Backdrops",
        description:
          "Step-and-repeat banners, media walls, and branded photo backdrops for product launches, activations, press events, and corporate functions.",
      },
    ],
    benefits: [
      {
        title: "Maximum Visual Impact",
        description:
          "Giant-scale graphics stop people in their tracks. Perfect for high-traffic retail, trade shows, and public spaces where first impressions count.",
      },
      {
        title: "Versatile Across Environments",
        description:
          "Works indoors and outdoors for events, retail, hospitality, and corporate settings. One solution adaptable to many applications.",
      },
      {
        title: "Fully Managed Process",
        description:
          "We handle supplier sourcing, artwork specifications, print management, and delivery coordination so you get flawless results without the complexity.",
      },
      {
        title: "Better Pricing Through Buying Power",
        description:
          "Our agency relationships with top Australian print suppliers give you access to trade pricing unavailable to businesses going direct.",
      },
    ],
  },
  {
    slug: "banners-flags",
    title: "Banners & Flags",
    img: "https://images.unsplash.com/photo-1627907192800-5df9708e7e87?w=600&q=80",
    tags: ["Events", "Durable", "Portable"],
    shortDesc: "Pull-up banners, event flags, and outdoor signage.",
    subtitle: "Portable brand power for any event or location.",
    intro:
      "Banners and flags are among the most versatile and cost-effective marketing tools available. From pull-up banners in a showroom to teardrop flags lining a festival entrance, All In Solutions sources the right product, manages the print, and delivers a solution that makes your brand impossible to miss, wherever your audience is.",
    options: [
      {
        title: "Pull-Up / Retractable Banners",
        description:
          "Spring-loaded retractable stands with high-quality print panels, compact for storage, fast to set up, and professional in any setting.",
      },
      {
        title: "Teardrop & Feather Flags",
        description:
          "Eye-catching flag styles designed to attract attention outdoors. Available in ground spike, cross-base, and wall-mount configurations.",
      },
      {
        title: "Event Flags & Bunting",
        description:
          "Large event flags, string bunting, and custom flag runs that create atmosphere and brand consistency at outdoor events and festivals.",
      },
      {
        title: "Outdoor PVC Banners",
        description:
          "Heavy-duty, weather-resistant banners with welded edges and eyelets, built for outdoor fencing, building frontages, and scaffolding.",
      },
    ],
    benefits: [
      {
        title: "Instant Brand Presence",
        description:
          "Deploy professional branded graphics in minutes. No installation crew, no tools. Unpack and brand your space immediately.",
      },
      {
        title: "Reusable & Cost-Effective",
        description:
          "High-quality banner hardware is designed to last through hundreds of events. Only reprint the graphic when your message changes.",
      },
      {
        title: "Works Anywhere",
        description:
          "From trade show floors to outdoor festivals, car parks to showrooms, banners and flags work in virtually any environment.",
      },
      {
        title: "Fast Lead Times",
        description:
          "Most banner and flag products are available with rapid turnaround times, perfect for last-minute event needs or urgent campaigns.",
      },
    ],
  },
  {
    slug: "offset-print",
    title: "Offset Print",
    img: "/offset-print-machine.jpg",
    tags: ["Bulk", "Premium", "Stationery"],
    shortDesc: "Brochures, catalogues, business cards, high-quality print runs.",
    subtitle: "Premium quality print for every business touchpoint.",
    intro:
      "Offset printing remains the gold standard for high-volume, high-quality printed materials. When you need brochures that feel luxurious, catalogues with accurate colour reproduction, or business cards that leave a lasting impression, offset delivers results that digital alternatives simply cannot match. All In Solutions manages the entire procurement process, ensuring your brand is represented exactly as it should be.",
    options: [
      {
        title: "Brochures & Flyers",
        description:
          "DL, A5, A4, and custom-folded formats across a full range of paper stocks and finishes, from economy to ultra-premium coated stock.",
      },
      {
        title: "Catalogues & Magazines",
        description:
          "Saddle-stitched, perfect-bound, or spiral-bound catalogues with vibrant, consistent colour reproduction across every page.",
      },
      {
        title: "Business Cards & Stationery",
        description:
          "Letterheads, envelopes, compliment slips, and business cards with optional premium finishes including spot UV, foil, embossing, and soft-touch laminate.",
      },
      {
        title: "Presentation Folders",
        description:
          "Custom-branded document folders with card slits, perfect for proposals, welcome packs, and sales presentations.",
      },
      {
        title: "Annual Reports & Corporate Documents",
        description:
          "High-specification bound documents that communicate the quality and professionalism of your organisation to every stakeholder.",
      },
    ],
    benefits: [
      {
        title: "Superior Colour Accuracy",
        description:
          "Offset printing reproduces your brand colours with precision that digital printing cannot match, essential for brand-consistent marketing materials.",
      },
      {
        title: "Cost-Effective at Volume",
        description:
          "The higher the quantity, the lower the unit cost. Offset is the smart choice for print runs of 500+ units.",
      },
      {
        title: "Premium Finish Options",
        description:
          "Choose from matte, gloss, silk, soft-touch laminate, spot UV, foil stamping, and embossing to elevate your printed materials above the competition.",
      },
      {
        title: "Professional Brand Impression",
        description:
          "High-quality printed collateral signals professionalism and builds trust with customers, partners, and stakeholders from the first touch.",
      },
    ],
  },
  {
    slug: "labels",
    title: "Labels",
    img: "https://images.unsplash.com/photo-1603712521905-3c68898026f2?w=600&q=80",
    tags: ["Die-Cut", "Rolls", "Waterproof"],
    shortDesc: "Product labels, stickers, custom packaging labels.",
    subtitle: "Labels that sell your product before you say a word.",
    intro:
      "A great label does more than identify a product: it communicates quality, builds brand recognition, and influences purchase decisions at the critical moment of choice. Whether you need retail product labels, compliance labels for regulated industries, or promotional stickers for a campaign, All In Solutions sources the right solution and manages production from artwork approval to delivery.",
    options: [
      {
        title: "Product Labels",
        description:
          "Sheet or roll format labels for bottles, jars, packaging, and retail products, available in paper, vinyl, clear, and specialty materials.",
      },
      {
        title: "Die-Cut Stickers",
        description:
          "Custom-shaped stickers cut precisely to your design, ideal for promotional giveaways, product branding, and creative brand applications.",
      },
      {
        title: "Packaging Labels",
        description:
          "Peel-and-stick labels for boxes, bags, and containers, including QR codes, barcodes, and variable data printing options.",
      },
      {
        title: "Barcode & Compliance Labels",
        description:
          "Industry-standard barcode labels, GS1-compliant formats, and regulatory compliance labels for manufacturing and distribution environments.",
      },
      {
        title: "Specialty & Premium Labels",
        description:
          "Foil, holographic, textured, waterproof, and cold-temperature resistant labels for premium products and demanding applications.",
      },
    ],
    benefits: [
      {
        title: "Shelf Presence That Sells",
        description:
          "A professionally printed label is the difference between a product that gets picked up and one that gets passed over at the exact moment of decision.",
      },
      {
        title: "Endless Material Options",
        description:
          "Waterproof, oil-resistant, cold-temperature, clear, foil: we source the right material for your product and environment every time.",
      },
      {
        title: "Small Runs to Large Volumes",
        description:
          "From short runs for boutique products to hundreds of thousands of labels for national distribution, we scale precisely to your needs.",
      },
      {
        title: "Variable Data & Personalisation",
        description:
          "Print unique barcodes, serial numbers, names, or promotional codes on every single label without slowing down production.",
      },
    ],
  },
  {
    slug: "promo-products",
    title: "Promo Products",
    img: "https://images.unsplash.com/photo-1606498679463-30a0eb8824e1?w=600&q=80",
    tags: ["Branded", "Gifts", "Merchandise"],
    shortDesc: "Branded merchandise, corporate gifts, promotional giveaways.",
    subtitle: "Branded merchandise people actually want to keep.",
    intro:
      "Promotional products create lasting brand impressions long after a handshake or an event. The right branded merchandise keeps your name in front of customers, rewards staff loyalty, and turns clients into brand advocates. All In Solutions gives you access to thousands of product categories, all sourced, branded, and delivered under one roof with zero supplier management on your end.",
    options: [
      {
        title: "Corporate Gifts",
        description:
          "Premium branded gifts for clients and partners, including leather goods, tech accessories, gourmet hampers, and fully custom gift sets.",
      },
      {
        title: "Event Giveaways",
        description:
          "Cost-effective branded items for trade shows, conferences, and activations: pens, bags, keyrings, drink bottles, tote bags, and much more.",
      },
      {
        title: "Staff Recognition Products",
        description:
          "Branded merchandise for team rewards, onboarding welcome packs, long-service recognition, and staff incentive programs.",
      },
      {
        title: "Branded Tech & Accessories",
        description:
          "USB drives, wireless chargers, earbuds, power banks, and other tech products branded with your logo to impressive effect.",
      },
      {
        title: "Sustainable & Eco Products",
        description:
          "Recyclable, organic, and sustainably sourced promotional products for brands committed to environmental responsibility and conscious marketing.",
      },
    ],
    benefits: [
      {
        title: "Brand Recall That Lasts",
        description:
          "Branded merchandise delivers among the lowest cost per impression of any advertising medium, and recipients keep useful items for months or years.",
      },
      {
        title: "Thousands of Product Options",
        description:
          "One conversation with All In Solutions unlocks access to thousands of products across hundreds of categories without hours of supplier research.",
      },
      {
        title: "Consistent Brand Application",
        description:
          "We manage artwork approval, brand colour matching, and quality checks to ensure every product accurately reflects your brand.",
      },
      {
        title: "Event-Ready Delivery",
        description:
          "We coordinate production timelines to ensure your promotional products arrive before your event. No last-minute stress, ever.",
      },
    ],
  },
  {
    slug: "signage",
    title: "Signage",
    img: "https://images.unsplash.com/photo-1517492973030-a02d282206f7?w=600&q=80",
    tags: ["Acrylic", "Illuminated", "Wayfinding"],
    shortDesc: "Indoor and outdoor signs, shop fronts, way-finding.",
    subtitle: "Signage that works for your brand around the clock.",
    intro:
      "Signage is one of the most powerful long-term marketing investments a business can make. A well-crafted sign works 24 hours a day, 365 days a year, guiding customers to your door and reinforcing your brand at every touchpoint. All In Solutions manages the full signage process: concept, sourcing, production, and installation coordination from one point of contact.",
    options: [
      {
        title: "Shop Front & Fascia Signage",
        description:
          "External building signage including illuminated and non-illuminated fascia signs, halo-lit letters, and acrylic panel systems.",
      },
      {
        title: "Window & Glass Graphics",
        description:
          "Vinyl window graphics, frosted privacy film, full-colour window prints, and one-way vision film for retail and commercial applications.",
      },
      {
        title: "Wayfinding & Directional Signs",
        description:
          "Interior and exterior directional signage systems that guide visitors through large spaces: offices, hospitals, shopping centres, and campuses.",
      },
      {
        title: "A-Frames & Pavement Signs",
        description:
          "Portable pavement signs, snap frames, and A-frames for promotions, daily specials, and directional use at street level.",
      },
      {
        title: "Illuminated & LED Signage",
        description:
          "Lightbox signs, LED channel letters, neon-effect signs, and backlit panels for maximum visibility day and night.",
      },
    ],
    benefits: [
      {
        title: "24/7 Brand Visibility",
        description:
          "Unlike advertising that runs on a schedule, quality signage promotes your business around the clock at no ongoing or recurring cost.",
      },
      {
        title: "Drives Foot Traffic",
        description:
          "Clear, professional signage is a proven driver of walk-in customers, directly impacting revenue for retail, hospitality, health, and service businesses.",
      },
      {
        title: "Professional Business Presence",
        description:
          "Quality signage communicates credibility and attention to detail, two factors that directly influence customer trust and purchasing decisions.",
      },
      {
        title: "One-Stop Project Management",
        description:
          "From concept and design coordination to production, compliance, and installation management, we handle every step of the signage project.",
      },
    ],
  },
  {
    slug: "it-services",
    title: "IT Services",
    img: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80",
    tags: ["Software", "Apps", "Websites"],
    shortDesc: "Software, apps, and websites for internal or external business use.",
    subtitle: "Technology solutions that power your business forward.",
    intro:
      "In today's competitive landscape, the right technology is not just an advantage; it is a necessity. All In Solutions connects businesses with vetted development partners to deliver custom software, mobile applications, websites, and internal business tools. Whether you need a customer-facing e-commerce platform or a private internal dashboard to streamline operations, we scope the project, source the right talent, and manage delivery from brief to launch.",
    options: [
      {
        title: "Business Websites",
        description:
          "Professional, mobile-responsive websites built to convert visitors into customers, from single-page brochure sites to complex multi-page platforms.",
      },
      {
        title: "E-Commerce Solutions",
        description:
          "Online stores with secure payment integration, inventory management, and seamless customer experience, built to sell at scale.",
      },
      {
        title: "Mobile Applications",
        description:
          "iOS and Android apps for customer engagement, loyalty programs, service booking, and internal team communication and management.",
      },
      {
        title: "Custom Business Software",
        description:
          "Bespoke software solutions tailored to your exact processes: CRM systems, quoting tools, inventory management, and workflow automation.",
      },
      {
        title: "Internal Business Tools",
        description:
          "Staff portals, reporting dashboards, booking systems, and operational tools that reduce manual work and dramatically improve team efficiency.",
      },
    ],
    benefits: [
      {
        title: "Streamline Your Operations",
        description:
          "Custom software eliminates repetitive manual tasks, reduces human error, and gives your team the tools to work faster and smarter every day.",
      },
      {
        title: "Improve Customer Experience",
        description:
          "A well-built website or app makes it easy for customers to find you, understand your offering, and do business with you any time, on any device.",
      },
      {
        title: "Scalable as You Grow",
        description:
          "Solutions are built with your future in mind, designed to grow with your business without expensive rewrites or painful platform migrations.",
      },
      {
        title: "Fully Scoped & Managed",
        description:
          "We translate your business requirements into a clear technical brief, source the right development partner, and oversee the full project through to launch.",
      },
    ],
  },
  {
    slug: "marketing",
    title: "Marketing",
    img: "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=600&q=80",
    tags: ["Direct Mail", "Strategy", "Collateral"],
    shortDesc: "Direct mail campaigns, marketing collateral, campaign management.",
    subtitle: "Campaigns that connect, convert, and build lasting relationships.",
    intro:
      "Great marketing doesn't happen by accident. It starts with a clear strategy, the right creative assets, and flawless execution across every channel. All In Solutions bridges the gap between your marketing ambitions and real-world delivery, managing campaigns, sourcing creative talent, and coordinating print and digital collateral so your message reaches the right people at exactly the right time.",
    options: [
      {
        title: "Direct Mail Campaigns",
        description:
          "Targeted letterbox campaigns, personalised mailers, and catalogue drops, managed end to end from data targeting to doorstep delivery.",
      },
      {
        title: "Marketing Collateral",
        description:
          "Brochures, presentation folders, leave-behind materials, and display stands that equip your sales team with professional, brand-consistent tools.",
      },
      {
        title: "Email Marketing (EDMs)",
        description:
          "Branded email campaigns designed to nurture leads, retain customers, and drive consistent repeat business.",
      },
      {
        title: "Campaign Planning & Strategy",
        description:
          "We help map campaign objectives, target audiences, and channel mix, ensuring every dollar of your marketing budget works as hard as possible.",
      },
      {
        title: "Social Media & Digital Assets",
        description:
          "Social media graphics, digital advertising assets, and online collateral that extend your campaign reach across digital channels.",
      },
    ],
    benefits: [
      {
        title: "Integrated Print + Digital Reach",
        description:
          "Combine the proven impact of direct mail with digital channels to create campaigns that reach customers wherever they are.",
      },
      {
        title: "Measurable Results",
        description:
          "Every campaign we manage is built with tracking and measurement in mind so you know exactly what is working and can act on it.",
      },
      {
        title: "One Agency, Every Channel",
        description:
          "From concept to collateral to campaign delivery, All In Solutions coordinates every element so nothing falls through the cracks.",
      },
      {
        title: "Increased Customer Lifetime Value",
        description:
          "Smart retention marketing keeps existing customers engaged and coming back, often at a fraction of the cost of acquiring new ones.",
      },
    ],
  },
];
